/**
 * Network — Warm-intro graph pagina (Sprint 2 · Feature #3)
 *
 * Team-members importeren hun LinkedIn connections (CSV export) en zien
 * per bedrijf welke collega's iemand kennen daar. Klik op een connectie →
 * copy-paste een intro-request template.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { PageHeader } from "./Dashboard";
import { toast } from "sonner";
import {
    UsersRound, Upload, Search as SearchIcon, Copy, Check, Loader2, Building2, Linkedin, Mail,
} from "lucide-react";

export default function Network() {
    const { t } = useTranslation();
    const [connections, setConnections] = useState([]);
    const [loading, setLoading] = useState(true);
    const [summary, setSummary] = useState([]);
    const [companyFilter, setCompanyFilter] = useState("");
    const [nameFilter, setNameFilter] = useState("");
    const [warmIntros, setWarmIntros] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [introOpen, setIntroOpen] = useState(null);   // {conn, company}

    const load = () => {
        setLoading(true);
        Promise.all([
            api.get("/network/connections", { params: { limit: 500 } }),
            api.get("/network/team-summary"),
        ]).then(([a, b]) => {
            setConnections(a.data || []);
            setSummary(b.data || []);
        }).finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, []);

    const filtered = useMemo(() => {
        return connections.filter((c) => {
            if (companyFilter && !((c.connection_company || "").toLowerCase().includes(companyFilter.toLowerCase()))) return false;
            if (nameFilter && !((c.connection_name || "").toLowerCase().includes(nameFilter.toLowerCase()))) return false;
            return true;
        });
    }, [connections, companyFilter, nameFilter]);

    const onUpload = async (file) => {
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await api.post("/network/connections/import-csv", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success(t("network.import_done", { inserted: r.data.inserted, skipped: r.data.skipped }));
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("network.upload_failed"));
        } finally { setUploading(false); }
    };

    const lookupWarmIntros = async () => {
        if (!companyFilter.trim()) {
            toast.error(t("network.type_company_first"));
            return;
        }
        try {
            const r = await api.get("/network/warm-intros", { params: { company: companyFilter.trim() } });
            setWarmIntros(r.data);
        } catch (e) {
            toast.error(t("network.search_failed"));
        }
    };

    return (
        <PageHeader title={t("network.title")} subtitle={t("network.subtitle")}>
            {/* Upload block */}
            <div className="rounded-xl border border-white/8 bg-[var(--jr-surface)] p-4 mb-4">
                <div className="flex items-center gap-3 flex-wrap">
                    <UsersRound size={16} className="text-[var(--jr-brand)]"/>
                    <span className="text-xs text-[var(--jr-text-2)]">
                        {t("network.export_hint_1")} <a href="https://www.linkedin.com/mypreferences/d/download-my-data" target="_blank" rel="noreferrer" className="underline text-[var(--jr-brand)]">LinkedIn Data Download</a> {t("network.export_hint_2")} <b>Connections.csv</b> {t("network.export_hint_3")}
                    </span>
                </div>
                <div className="mt-3 flex items-center gap-2">
                    <label className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--jr-brand)] text-black text-xs font-medium hover:bg-[var(--jr-brand-2)] cursor-pointer">
                        {uploading ? <Loader2 size={12} className="animate-spin"/> : <Upload size={12}/>}
                        {uploading ? t("network.uploading") : t("network.choose_csv")}
                        <input type="file" accept=".csv" hidden data-testid="network-csv-upload"
                            onChange={(e) => onUpload(e.target.files?.[0])}/>
                    </label>
                    <span className="text-[11px] text-[var(--jr-text-3)]">{t("network.upload_note")}</span>
                </div>
            </div>

            {/* Team summary */}
            <div className="grid md:grid-cols-3 gap-3 mb-4">
                {summary.map((s) => (
                    <div key={s._id || s.user_id} className="rounded-xl border border-white/8 bg-[var(--jr-surface)] p-4">
                        <div className="text-xs text-[var(--jr-text-3)] uppercase tracking-[0.16em] font-mono">{t("network.team_member")}</div>
                        <div className="mt-1 font-display text-lg tracking-tight text-white">{s.user_name}</div>
                        <div className="text-sm text-[var(--jr-brand)] mt-1">{s.total} {t("network.connections")}</div>
                    </div>
                ))}
                {summary.length === 0 && !loading && (
                    <div className="rounded-xl border border-dashed border-white/10 p-6 text-center text-sm text-[var(--jr-text-3)] md:col-span-3">
                        {t("network.no_connections")}
                    </div>
                )}
            </div>

            {/* Warm-intro lookup */}
            <div className="rounded-xl border border-white/8 bg-[var(--jr-surface)] p-4 mb-4">
                <div className="text-xs font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)] mb-2">{t("network.warm_lookup")}</div>
                <div className="flex items-center gap-2 flex-wrap">
                    <div className="flex items-center gap-2 bg-[var(--jr-surface-2)] border border-white/10 rounded-full px-3 py-1.5 min-w-[240px]">
                        <Building2 size={12} className="text-[var(--jr-text-3)]"/>
                        <input value={companyFilter} onChange={(e) => setCompanyFilter(e.target.value)}
                            placeholder={t("network.company_placeholder")}
                            data-testid="network-warm-company"
                            className="bg-transparent text-sm text-white outline-none flex-1 placeholder:text-[var(--jr-text-3)]"/>
                    </div>
                    <button onClick={lookupWarmIntros}
                        data-testid="network-warm-lookup"
                        className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-[var(--jr-brand)] text-black text-xs font-medium hover:bg-[var(--jr-brand-2)]">
                        <SearchIcon size={12}/> {t("network.search_warm")}
                    </button>
                </div>
                {warmIntros && (
                    <div className="mt-4">
                        <div className="text-sm text-white mb-2">
                            <span className="text-[var(--jr-brand)] font-medium">{warmIntros.total_matches}</span> {t("network.connections_at")} <b>{warmIntros.company}</b>
                        </div>
                        <div className="space-y-2">
                            {warmIntros.owners.map((o) => (
                                <div key={o.user_id} className="rounded-lg border border-white/10 bg-[var(--jr-surface-2)] p-3">
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="text-sm font-medium text-white">{o.user_name}</div>
                                        <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-[var(--jr-brand)]/15 text-[var(--jr-brand)]">
                                            {o.connections.length} {t("network.contacts")}
                                        </span>
                                    </div>
                                    <ul className="space-y-1">
                                        {o.connections.map((c) => (
                                            <li key={c.id} className="flex items-center justify-between gap-2 text-xs">
                                                <div className="min-w-0 flex items-center gap-2 flex-1">
                                                    <span className="text-white truncate">{c.name}</span>
                                                    {c.title && <span className="text-[var(--jr-text-3)] truncate">— {c.title}</span>}
                                                    {c.url && (
                                                        <a href={c.url} target="_blank" rel="noreferrer" className="text-[#0a66c2]"><Linkedin size={11}/></a>
                                                    )}
                                                </div>
                                                <button onClick={() => setIntroOpen({ conn: c, company: warmIntros.company })}
                                                    data-testid={`network-intro-btn-${c.id}`}
                                                    className="text-[10px] px-2 py-0.5 rounded-full bg-[var(--jr-brand)]/15 text-[var(--jr-brand)] border border-[var(--jr-brand)]/40 hover:bg-[var(--jr-brand)]/25 shrink-0">
                                                    Vraag intro
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                            {warmIntros.owners.length === 0 && (
                                <div className="text-center text-xs text-[var(--jr-text-3)] py-6">
                                    {t("network.nobody_knows", { company: warmIntros.company })}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* All connections (mine) */}
            <div className="rounded-xl border border-white/8 bg-[var(--jr-surface)] p-4">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                    <div className="text-xs font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">{t("network.my_connections")} · {filtered.length}</div>
                    <input value={nameFilter} onChange={(e) => setNameFilter(e.target.value)}
                        placeholder={t("network.name_placeholder")}
                        data-testid="network-my-search"
                        className="bg-[var(--jr-surface-2)] border border-white/10 rounded-full px-3 py-1 text-xs text-white placeholder:text-[var(--jr-text-3)] outline-none focus:border-[var(--jr-brand)]"/>
                </div>
                {loading ? (
                    <div className="text-center text-sm text-[var(--jr-text-3)] py-6"><Loader2 size={16} className="animate-spin inline mr-2"/>{t("common.loading")}</div>
                ) : (
                    <div className="grid md:grid-cols-2 gap-2 max-h-[400px] overflow-y-auto">
                        {filtered.slice(0, 200).map((c) => (
                            <div key={c.id} className="text-xs rounded border border-white/8 bg-[var(--jr-surface-2)] p-2">
                                <div className="flex items-center gap-2">
                                    <span className="text-white truncate flex-1">{c.connection_name}</span>
                                    {c.connection_url && <a href={c.connection_url} target="_blank" rel="noreferrer" className="text-[#0a66c2]"><Linkedin size={11}/></a>}
                                    {c.connection_email && <a href={`mailto:${c.connection_email}`} className="text-[var(--jr-info)]"><Mail size={11}/></a>}
                                </div>
                                <div className="text-[10px] text-[var(--jr-text-3)] mt-0.5 truncate">
                                    {c.connection_title || "?"} @ {c.connection_company || "?"}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {introOpen && <IntroModal conn={introOpen.conn} company={introOpen.company} onClose={() => setIntroOpen(null)}/>}
        </PageHeader>
    );
}


function IntroModal({ conn, company, onClose }) {
    const { t } = useTranslation();
    const [reason, setReason] = useState("");
    const [template, setTemplate] = useState("");
    const [copied, setCopied] = useState(false);
    const [loading, setLoading] = useState(false);

    const generate = async () => {
        setLoading(true);
        try {
            const r = await api.post("/network/intro-template", {
                connection_id: conn.id, company, reason: reason.trim() || null,
            });
            setTemplate(r.data.template);
        } catch (e) {
            toast.error(t("network.template_failed"));
        } finally { setLoading(false); }
    };
    useEffect(() => { generate(); }, [conn.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(template);
            setCopied(true); setTimeout(() => setCopied(false), 2000);
            toast.success(t("network.template_copied"));
        } catch (e) { toast.error(t("network.copy_failed")); }
    };

    return (
        <div className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm flex items-center justify-center p-6" onClick={onClose} data-testid="intro-modal-backdrop">
            <div className="w-full max-w-lg rounded-2xl border border-white/10 bg-[var(--jr-surface)] p-6" onClick={(e) => e.stopPropagation()}>
                <div className="text-xs font-mono uppercase tracking-[0.16em] text-[var(--jr-text-3)]">{t("network.intro_template")}</div>
                <div className="mt-1 text-lg font-display text-white">{t("network.ask_intro_to", { name: conn.name.split(" ")[0], company })}</div>
                <textarea value={reason} onChange={(e) => setReason(e.target.value)}
                    placeholder={t("network.reason_placeholder")}
                    className="mt-3 w-full min-h-[70px] bg-[var(--jr-surface-2)] border border-white/10 rounded px-3 py-2 text-xs text-white placeholder:text-[var(--jr-text-3)] outline-none focus:border-[var(--jr-brand)]"
                    data-testid="intro-reason"/>
                <button onClick={generate} disabled={loading}
                    data-testid="intro-regenerate"
                    className="mt-2 text-[11px] px-3 py-1 rounded-full border border-white/10 text-[var(--jr-text-2)] hover:bg-white/5">
                    {loading ? <Loader2 size={11} className="animate-spin inline mr-1"/> : null}
                    {t("network.rebuild_template")}
                </button>
                <pre className="mt-3 whitespace-pre-wrap text-xs text-[var(--jr-text-2)] rounded bg-[var(--jr-surface-2)] border border-white/10 p-3 max-h-[280px] overflow-y-auto">
                    {template || "…"}
                </pre>
                <div className="mt-4 flex justify-end gap-2">
                    <button onClick={onClose} className="text-xs px-3 py-1.5 rounded-full border border-white/10 text-[var(--jr-text-2)] hover:bg-white/5">
                        {t("common.close")}
                    </button>
                    <button onClick={copy} disabled={!template}
                        data-testid="intro-copy"
                        className={`text-xs px-4 py-1.5 rounded-full font-medium ${copied ? "bg-[#22c55e]/20 text-[#22c55e] border border-[#22c55e]/40" : "bg-[var(--jr-brand)] text-black hover:bg-[var(--jr-brand-2)]"}`}>
                        {copied ? <><Check size={12} className="inline mr-1"/> {t("network.copied")}</> : <><Copy size={12} className="inline mr-1"/> {t("network.copy")}</>}
                    </button>
                </div>
            </div>
        </div>
    );
}
